import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { styled } from "@mui/material/styles";

const ErrorIcon = styled(ErrorOutlineIcon)({
  color: "#d32f2f",
  fontSize: "20px",
  backgroundColor: "#ffebee",
  borderRadius: "50%",
  padding: "2px",
});

function EditLocationIdModal({
  open,
  onClose,
  pin,
  selectedDrawingForView,
  onSave,
}) {
  const [locationId, setLocationId] = useState("");
  const [error, setError] = useState("");

  // Initialize location ID from pin
  useEffect(() => {
    if (pin && open) {
      const initialLocationId = pin.location_id || pin.label || "";
      setLocationId(initialLocationId);
      setError("");
    }
  }, [pin, open]);

  // Get session information
  const sessionName = pin?.construction_recordings?.[0]?.session_name || "";
  const sessionNumber = sessionName?.split("_")[1] || "";
  const sessionCreatedAt = pin?.construction_recordings?.[0]?.created_at || pin?.timestamp;

  // Format date: "31 Dec 2025, 14:19"
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch (e) {
      return "";
    }
  };

  // Get drawing name
  const drawingName = selectedDrawingForView?.name || "Unknown";

  // Validate location ID
  const validateLocationId = (value) => {
    if (!value || value.trim() === "") {
      return "Location ID cannot be empty";
    }
    if (value.length > 20) {
      return "Location ID must be 20 characters or less";
    }
    // Allowed: Letters, numbers, hyphens, underscores
    const validPattern = /^[A-Za-z0-9_-]+$/;
    if (!validPattern.test(value)) {
      return "Location ID can only contain letters, numbers, hyphens, and underscores";
    }
    return "";
  };

  const handleLocationIdChange = (e) => {
    const value = e.target.value;
    setLocationId(value);
    const validationError = validateLocationId(value);
    setError(validationError);
  };

  const handleSave = () => {
    const validationError = validateLocationId(locationId);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (onSave) {
      onSave(locationId.trim());
    }
    onClose();
  };

  const handleClose = () => {
    setLocationId("");
    setError("");
    onClose();
  };

  // Preview text (truncate if longer than 20 chars)
  const previewText = locationId.length > 20 
    ? locationId.substring(0, 20) + "..." 
    : locationId || "...";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          bgcolor: "#ffffff",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Roboto, sans-serif",
          fontSize: "18px",
          fontWeight: 600,
          color: "#212121",
          pb: 1.5,
          pt: 2.5,
          px: 2.5,
        }}
      >
        Edit Location ID
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 0 }}>
        {/* Session Information Box */}
        <Box
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: "4px",
            p: 1.5,
            mb: 2.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          {/* Blue Square Icon */}
          <Box
            sx={{
              width: 24,
              height: 24,
              bgcolor: "#1976d2",
              borderRadius: "4px",
              flexShrink: 0,
              mt: 0.25,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: "#212121",
                mb: 0.5,
              }}
            >
              Session {sessionNumber}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                color: "#4a5565",
                mb: 0.25,
              }}
            >
              Drawing: {drawingName}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                color: "#4a5565",
              }}
            >
              Created: {formatDate(sessionCreatedAt)}
            </Typography>
          </Box>
        </Box>

        {/* Location ID Input */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "#212121",
              mb: 1,
            }}
          >
            Location ID
          </Typography>
          <TextField
            fullWidth
            value={locationId}
            onChange={handleLocationIdChange}
            placeholder="e.g., L12, ZONE-A, CUSTOM5"
            error={!!error}
            InputProps={{
              endAdornment: error ? (
                <InputAdornment position="end">
                  <ErrorIcon />
                </InputAdornment>
              ) : null,
              sx: {
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                bgcolor: "#f5f5f5",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: error ? "#d32f2f" : "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: error ? "#d32f2f" : "#bdbdbd",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: error ? "#d32f2f" : "#1976d2",
                },
              },
            }}
          />
          {error && (
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                color: "#d32f2f",
                mt: 0.75,
                ml: 0.5,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>

        {/* Preview Section */}
        <Box
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: "4px",
            p: 1.5,
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              color: "#4a5565",
              mb: 0.75,
            }}
          >
            Preview:
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#212121",
            }}
          >
            {previewText}
          </Typography>
        </Box>

        {/* Rules Section */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "13px",
              color: "#4a5565",
              mb: 0.75,
            }}
          >
            Rules:
          </Typography>
          <Box
            component="ul"
            sx={{
              margin: 0,
              paddingLeft: 2.5,
              "& li": {
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                color: "#4a5565",
                mb: 0.5,
              },
            }}
          >
            <li>Allowed: Letters, numbers, hyphens, underscores</li>
            <li>Max 20 characters</li>
            <li>Must be unique within this session</li>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          pb: 2.5,
          pt: 1.5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            color: "#4a5565",
            textTransform: "none",
            minWidth: "auto",
            px: 2,
            "&:hover": {
              bgcolor: "transparent",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!!error || !locationId.trim()}
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            fontWeight: 500,
            bgcolor: "#616161",
            color: "#ffffff",
            textTransform: "none",
            minWidth: "auto",
            px: 2.5,
            "&:hover": {
              bgcolor: "#424242",
            },
            "&.Mui-disabled": {
              bgcolor: "#e0e0e0",
              color: "#9e9e9e",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditLocationIdModal;

